// Movement decisions read held JSON and accumulate only changed branches. No before-image,
// serialization or history: discarding a decision simply discards its writes.
import type { JanusCtx, Row } from '../ability/context.js';

type JsonObject = Record<PropertyKey, unknown>;
interface Node {
  source: JsonObject; copy?: JsonObject; proxy: JsonObject;
  children: Map<PropertyKey, Node>; parents: Map<Node, Set<PropertyKey>>;
  result?: JsonObject;
}
export interface DecisionCounts { frames: number; branchCopies: number; memberPatches: number }
// A context may be handed to another engine bundle. The handles belong to the view,
// rather than a bundle-local registry that the receiving engine would duplicate.
const NODE = Symbol.for('fatetide.movementDecision.node');
const FINISH = Symbol.for('fatetide.movementDecision.finish');
type DecisionMembers = JanusCtx['members'] & { [FINISH]?: () => Row };
const nodeOf = (value: object): Node | undefined => (value as { [NODE]?: Node })[NODE];
const object = (v: unknown): v is JsonObject => v !== null && typeof v === 'object'
  && (Array.isArray(v) || Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);
const publish = (counts: DecisionCounts): void => {
  (globalThis as { ftMovementDecisionCount?: (counts: DecisionCounts) => void }).ftMovementDecisionCount?.(counts);
};
const owns = (value: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(value, key);
function shallow(source: JsonObject): JsonObject {
  if (Array.isArray(source)) return source.slice() as unknown as JsonObject;
  const out = { ...source };
  const base = Object.getOwnPropertyDescriptor(source, '_entity_base');
  if (base && !base.enumerable) Object.defineProperty(out, '_entity_base', base);
  return out;
}

function result(node: Node): JsonObject {
  if (!node.copy) return node.source;
  if (node.result) return node.result;
  const out = node.result = shallow(node.copy);
  publish({ frames: 0, branchCopies: 1, memberPatches: 0 });
  for (const [key, child] of node.children) if (owns(out, key)) out[key] = result(child);
  return out;
}

/** Borrow one already-held decision input without walking its untouched descendants. */
export function decisionRead<T>(value: T): T {
  const node = value && typeof value === 'object' ? nodeOf(value) : undefined;
  return node ? result(node) as T : value;
}

/** Strip decision handles from the small write payload, retaining every untouched value. */
export function decisionWrites<T>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  const node = nodeOf(value);
  if (node) return result(node) as T;
  if (!object(value)) return value;
  let out: JsonObject | undefined;
  for (const key of Object.keys(value)) {
    const before = value[key], after = decisionWrites(before);
    if (after !== before) {
      out ??= Array.isArray(value) ? value.slice() as unknown as JsonObject : { ...value };
      out[key] = after;
    }
  }
  return (out ?? value) as T;
}

/** A synchronous read view with a named finish operation. Reads allocate handles, never copies;
 * the first actual write copies its branch and parents, sharing catalogs/geometry/other members.
 * Finish before retaining a chain state or forking a face; never mutate a finished view. */
export function movementDecisionView(encounter: Row, held: ReadonlyMap<string, Row>):
  Pick<JanusCtx, 'encounter' | 'members' | 'originals'> {
  const memo = new WeakMap<object, Node>();
  const roots = new Set<Node>();
  const invalidate = (node: Node): void => {
    node.result = undefined;
    for (const parent of node.parents.keys()) invalidate(parent);
  };
  const change = (node: Node): void => {
    if (node.copy) return;
    node.copy = shallow(node.source);
    publish({ frames: 0, branchCopies: 1, memberPatches: roots.has(node) ? 1 : 0 });
    for (const parent of node.parents.keys()) change(parent);
  };
  const wrap = (source: JsonObject): Node => {
    const known = memo.get(source);
    if (known) return known;
    const node: Node = { source, proxy: {}, children: new Map(), parents: new Map() };
    memo.set(source, node);
    const target = Array.isArray(source) ? [] : {};
    node.proxy = new Proxy(target, {
      get(_target, key) {
        if (key === NODE) return node;
        const value = (node.copy ?? source)[key];
        if (!object(value)) return value;
        const own = nodeOf(value);
        // An input from another completed decision belongs to this decision from here on.
        const child = wrap(own ? result(own) : value);
        node.children.set(key, child);
        let keys = child.parents.get(node);
        if (!keys) child.parents.set(node, keys = new Set());
        keys.add(key);
        if (child.copy) change(node);
        return child.proxy;
      },
      set(_target, key, value) {
        const current = (node.copy ?? source)[key];
        if (Object.is(current, value) || node.children.get(key)?.proxy === value) return true;
        change(node);
        invalidate(node);
        detach(node, key);
        node.children.delete(key);
        node.copy![key] = decisionWrites(value);
        return true;
      },
      defineProperty(_target, key, descriptor) {
        return 'value' in descriptor ? Reflect.set(node.proxy, key, descriptor.value) : false;
      },
      deleteProperty(_target, key) {
        if (!owns(node.copy ?? source, key)) return true;
        change(node); invalidate(node); detach(node, key); node.children.delete(key); delete node.copy![key]; return true;
      },
      has: (_target, key) => key in (node.copy ?? source),
      ownKeys: () => Reflect.ownKeys(node.copy ?? source),
      getOwnPropertyDescriptor(_target, key) {
        const descriptor = Object.getOwnPropertyDescriptor(node.copy ?? source, key);
        return descriptor ? { ...descriptor, configurable: key !== 'length' || !Array.isArray(source) } : undefined;
      },
    }) as JsonObject;
    return node;
  };
  const detach = (parent: Node, key: PropertyKey): void => {
    const child = parent.children.get(key), keys = child?.parents.get(parent);
    keys?.delete(key);
    if (child && keys?.size === 0) child.parents.delete(parent);
  };
  const members = new Map<string, Row>(), originals = new Map<string, Row>();
  for (const [id, member] of held) {
    const own = nodeOf(member), source = own ? result(own) as Row : member;
    const node = wrap(source); roots.add(node);
    members.set(id.toUpperCase(), node.proxy as Row); originals.set(id.toUpperCase(), source);
  }
  const ownScene = nodeOf(encounter);
  const scene = wrap(ownScene ? result(ownScene) : encounter);
  Object.defineProperty(members, FINISH, { configurable: true, value: () => {
    for (const [id, member] of members) members.set(id, decisionWrites(member));
    return result(scene) as Row;
  } });
  publish({ frames: 1, branchCopies: 0, memberPatches: 0 });
  return { encounter: scene.proxy as Row, members, originals };
}

/** Publish this decision's changed member branches; unchanged members keep their held identity. */
export function finishMovementDecision(ctx: Pick<JanusCtx, 'encounter' | 'members'>): void {
  const finish = (ctx.members as DecisionMembers)[FINISH];
  if (!finish) return;
  ctx.encounter = finish();
  delete (ctx.members as DecisionMembers)[FINISH];
}
